
import { h, Fragment } from 'preact'
import classnames from 'classnames'
import { useState, useRef } from 'preact/hooks'
import { useDidMountEffect } from './../hooks/useDidMountEffect'
import { Checkbox } from './Checkbox'
import { FieldContainer } from './FieldContainer'


function toOption(option) {
	if (typeof option === 'string') 
		return { title: option, value: option }

	if (option === Object(option)) 
		return { 
			title: option.title || option.value, 
			value: option.value,
			description: option.description || '',
			input: option.input || false,
			inputName: option.inputName || '',
			inputText: option.inputText || '',
			inputValue: option.inputValue || '',
		}
	return {}
}


function FieldRadioOption({ title, description, name, value, checked, input, inputName, inputText, inputValue, onInputBlur, ...props }) {	

	if (!title && !value) return

	const inputRef = useRef()
	useDidMountEffect(() => {
		if (checked && inputRef.current) inputRef.current.focus()
	}, [checked])

	return <>
		<label class="radio radio--large">
			<input name={name} type="radio" value={value} checked={checked} {...props} /> 
			{title}
		</label>
		{description && <div class="radio__description">{description}</div>}
		{input && checked && <>
			<div class="radio__more">
				<label>
					{inputText && <span class="input__label">{inputText}</span>}
					<input 
						ref={inputRef} 
						name={inputName || name} 
						value={inputValue} 
						type="text" 
						onBlur={e => (event.target.value !== inputValue) && onInputBlur(e)} 
					/>
				</label>
			</div>
		</>}
	</>
}


export function FieldRadio({ name, value, options = [], or, error, onClick, onChange, ...props }) {

	const [checked, setChecked] = useState(value)
	
	// radios don't fire onChange, 
	// so just mock them to an internal click event
	const clickHandler = event => {
		setChecked(event.target.value)
		onClick && onClick(event)
		onChange && onChange(event)
	}

	const optionToRadio = option => {
		const radio = toOption(option)
		radio.key = radio.name
		radio.onClick = clickHandler
		radio.onInputBlur = onChange
		radio.checked = checked === radio.value
		radio.name = name
		return radio
	}

	return <FieldContainer class="margin-y-3" error={error}>
		{options.map(option => <FieldRadioOption {...optionToRadio(option)} />)}
		{or && <>
			<div class="radio__or">Or</div>
			<FieldRadioOption {...optionToRadio(or)} />
		</>}
	</FieldContainer>
}


function FieldCheckboxOption({ title, description, value, checked, input, inputName, inputText, inputValue, onInputBlur, ...props }) {	

	if (!title && !value) return

	const inputRef = useRef()
	useDidMountEffect(() => {
		if (checked && inputRef.current) inputRef.current.focus()
	}, [checked])

	return <>
		<Checkbox larger name={name} value={value} checked={checked} label={title} {...props} />
		{description && <div class="checkbox__description">{description}</div>}
		{input && checked && <>
			<div class="checkbox__more">
				{inputText && <span class="input__label">{inputText}</span>}
				<input 
					ref={inputRef} 
					name={inputName || name} 
					value={inputValue} 
					type="text" 
					onBlur={e => (event.target.value !== inputValue) && onInputBlur(e)} 
				/>
			</div>
		</>}
	</>
}


export function FieldCheckboxes({ name, values = [], options = [], or, error, onChange, ...props }) {

	const [checked, setChecked] = useState(values)
	const add = value => setChecked([...checked, value])
	const remove = value => setChecked(checked.filter(x => x !== value))
	const changeHandler = (event, checked) => {
		checked ? add(event.target.value) : remove(event.target.value)
		onChange && onChange(event, checked)
	}

	const optionToCheckbox = option => {
		const check = toOption(option)
		check.name = name
		check.key = check.name
		check.onChange = changeHandler
		check.onInputBlur = onChange
		check.checked = checked.includes(check.value)
		return check
	}

	return <FieldContainer class="margin-y-3" error={error}>
		{options.map(option => <FieldCheckboxOption {...optionToCheckbox(option)} />)}
		{or && <>
			<div class="checkbox__or">Or</div>
			<FieldCheckboxOption {...optionToCheckbox(or)} />
		</>}
	</FieldContainer>
}


export function FieldSelect({ label, name, value, options = [], initial = 'Please select…', error, children, ...props }) {

	const selected = value

	const Option = ({ title, value }) => (title && value) && <>
		<option value={value} selected={selected === value}>{title}</option>
	</>

	return <>
		<div class="input">
			<label class="inline-block">
				{label && <div class="input__label">{label}</div>}
				<select name={name} {...props}>
					{initial && <>
						<option>{initial}</option>
						<option disabled></option>
					</>}
					{options.map(option => <Option {...toOption(option)} />)}
					{children}
				</select>
			</label>
			{error && <div><span class="form-error">{error}</span></div>}
		</div>
	</>
}